// src/pages/ConfigPage.js
import React, { useState, useEffect } from 'react';
import {
  Eye, EyeOff, Save, HelpCircle, CheckCircle2, AlertCircle,
  X, Loader2, Settings, Plug, KeyRound, ShieldCheck,
  ChevronRight, BookOpen,
} from 'lucide-react';
import api from '../api/api';
import styles from './ConfigPage.module.css';

// ─────────────────────────────────────────────────────────────
// DONNÉES
// ─────────────────────────────────────────────────────────────
const TABS = [
  {
    id: 'boondmanager',
    label: 'BoondManager',
    Icon: Plug,
    fields: ['user_token', 'client_token', 'client_key'],
    color: 'blue',
  },
  {
    id: 'hubspot',
    label: 'HubSpot',
    Icon: Settings,
    fields: ['hubspot_dev_key'],
    color: 'orange',
  },
  {
    id: 'myhub',
    label: 'MyHubConsulting',
    Icon: ShieldCheck,
    fields: ['myhub_api_key'],
    color: 'green',
  },
];

const FIELD_META = {
  user_token:     { label: 'User Token',     placeholder: 'Entrez votre User Token' },
  client_token:   { label: 'Client Token',   placeholder: 'Entrez votre Client Token' },
  client_key:     { label: 'Client Key',     placeholder: 'Entrez votre Client Key' },
  hubspot_dev_key:{ label: 'Clé Développeur Privée', placeholder: 'pat-eu1-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx' },
  myhub_api_key:  { label: 'Clé API',        placeholder: 'Entrez votre clé API MyHubConsulting' },
};

const TUTORIALS = {
  boondmanager: {
    title: 'Récupérer vos identifiants BoondManager',
    steps: [
      'Connectez-vous à BoondManager en tant qu\'Administrateur.',
      { strong: 'User Token :', text: 'Cliquez sur votre avatar (haut à droite) → Mon compte → rubrique API.' },
      { strong: 'Client Token & Key :', text: 'Administration → Espace développeur → API / SandBox. Activez l\'authentification X-Jwt-Client.' },
    ],
  },
  hubspot: {
    title: 'Créer une application privée HubSpot',
    steps: [
      'Dans HubSpot, ouvrez les Paramètres (roue dentée).',
      'Menu gauche : Développement → Applications héritées.',
      'Cliquez sur "Créer une application héritée".',
      { strong: 'Scopes requis :', text: 'crm.objects.contacts.read, crm.objects.contacts.write, files' },
      'Validez et copiez le jeton d\'accès affiché.',
    ],
  },
  myhub: {
    title: 'Récupérer votre Clé API MyHubConsulting',
    steps: [
      'Connectez-vous à votre espace administrateur MyHubConsulting.',
      'Rendez-vous dans Configuration → API.',
      'Générez une nouvelle clé ou copiez la clé existante.',
      'Collez-la dans le champ ci-dessous.',
    ],
  },
};

// ─────────────────────────────────────────────────────────────
// COMPOSANT PRINCIPAL
// ─────────────────────────────────────────────────────────────
const ConfigPage = () => {
  const [config, setConfig] = useState({
    user_token: '', client_token: '', client_key: '',
    hubspot_dev_key: '', myhub_api_key: '',
  });
  const [showPasswords, setShowPasswords]   = useState({});
  const [showTutorial, setShowTutorial]     = useState({});
  const [activeTab, setActiveTab]           = useState('boondmanager');
  const [isLoading, setIsLoading]           = useState(true);
  const [isSaving, setIsSaving]             = useState(false);
  const [notification, setNotification]     = useState(null);

  // ── Fetch ─────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/config/boondmanager');
        setConfig({
          user_token:      data.user_token      || '',
          client_token:    data.client_token    || '',
          client_key:      data.client_key      || '',
          hubspot_dev_key: data.hubspot_dev_key || '',
          myhub_api_key:   data.myhub_api_key   || '',
        });
      } catch {
        setNotification({ type: 'error', message: 'Erreur lors du chargement de la configuration.' });
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  // ── Handlers ──────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setConfig((p) => ({ ...p, [name]: value }));
  };

  const togglePassword = (field) =>
    setShowPasswords((p) => ({ ...p, [field]: !p[field] }));

  const toggleTutorial = (tab) =>
    setShowTutorial((p) => ({ ...p, [tab]: !p[tab] }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setNotification(null);
    try {
      await api.put('/config/boondmanager', config);
      setNotification({ type: 'success', message: 'Configuration enregistrée avec succès !' });
    } catch (err) {
      setNotification({
        type: 'error',
        message: err.response?.data?.message || 'Une erreur est survenue.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const currentTab   = TABS.find((t) => t.id === activeTab);
  const currentTuto  = TUTORIALS[activeTab];
  const filledFields = currentTab?.fields.filter((f) => config[f]?.trim()).length ?? 0;
  const totalFields  = currentTab?.fields.length ?? 0;

  // ── Loading ───────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className={styles.loadingState}>
        <div className={styles.loadingCard}>
          <Loader2 size={32} className={styles.loadingSpinner} />
          <p>Chargement de la configuration…</p>
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────
  return (
    <div className={styles.page}>
      {/* ── En-tête de page ── */}
      <div className={styles.pageHeader}>
        <div className={styles.pageHeaderIcon}>
          <KeyRound size={22} strokeWidth={1.75} />
        </div>
        <div>
          <h1 className={styles.pageTitle}>Connecteurs API</h1>
          <p className={styles.pageSubtitle}>
            Gérez vos identifiants d'intégration en toute sécurité
          </p>
        </div>
      </div>

      {/* ── Notification ── */}
      {notification && (
        <div className={`${styles.notification} ${styles[`notification${notification.type === 'success' ? 'Success' : 'Error'}`]}`}>
          <div className={styles.notifContent}>
            {notification.type === 'success'
              ? <CheckCircle2 size={16} strokeWidth={2} />
              : <AlertCircle   size={16} strokeWidth={2} />}
            <span>{notification.message}</span>
          </div>
          <button className={styles.notifClose} onClick={() => setNotification(null)} aria-label="Fermer">
            <X size={14} strokeWidth={2.5} />
          </button>
        </div>
      )}

      <div className={styles.layout}>
        {/* ════════ SIDEBAR TABS ════════ */}
        <aside className={styles.sidebar}>
          <p className={styles.sidebarLabel}>Intégrations</p>
          <nav className={styles.tabList}>
            {TABS.map(({ id, label, Icon }) => {
              const fields = TABS.find((t) => t.id === id).fields;
              const filled = fields.filter((f) => config[f]?.trim()).length;
              const isComplete = filled === fields.length && fields.length > 0;
              return (
                <button
                  key={id}
                  type="button"
                  className={`${styles.tabItem} ${activeTab === id ? styles.tabItemActive : ''}`}
                  onClick={() => setActiveTab(id)}
                >
                  <span className={`${styles.tabIcon} ${activeTab === id ? styles.tabIconActive : ''}`}>
                    <Icon size={16} strokeWidth={1.75} />
                  </span>
                  <span className={styles.tabLabel}>{label}</span>
                  {isComplete && (
                    <span className={styles.tabBadgeOk} title="Configuré">
                      <CheckCircle2 size={13} strokeWidth={2.5} />
                    </span>
                  )}
                  {activeTab === id && (
                    <ChevronRight size={14} strokeWidth={2} className={styles.tabChevron} />
                  )}
                </button>
              );
            })}
          </nav>

          {/* Stat mini */}
          <div className={styles.sidebarStats}>
            <div className={styles.statRow}>
              <span className={styles.statLabel}>Champs remplis</span>
              <span className={styles.statValue}>{filledFields}/{totalFields}</span>
            </div>
            <div className={styles.statBar}>
              <div
                className={styles.statBarFill}
                style={{ width: totalFields ? `${(filledFields / totalFields) * 100}%` : '0%' }}
              />
            </div>
          </div>
        </aside>

        {/* ════════ FORMULAIRE ════════ */}
        <main className={styles.formCard}>
          <form onSubmit={handleSubmit} noValidate>
            {/* En-tête section */}
            <div className={styles.sectionHead}>
              <div className={styles.sectionHeadLeft}>
                <div className={`${styles.sectionIconWrap} ${styles[`sectionIcon_${currentTab?.color}`]}`}>
                  {currentTab && <currentTab.Icon size={18} strokeWidth={1.75} />}
                </div>
                <div>
                  <h2 className={styles.sectionTitle}>{currentTab?.label}</h2>
                  <p className={styles.sectionSub}>
                    {filledFields === totalFields
                      ? '✓ Tous les champs sont renseignés'
                      : `${totalFields - filledFields} champ(s) manquant(s)`}
                  </p>
                </div>
              </div>

              {/* Bouton aide */}
              <button
                type="button"
                className={`${styles.helpBtn} ${showTutorial[activeTab] ? styles.helpBtnActive : ''}`}
                onClick={() => toggleTutorial(activeTab)}
              >
                <BookOpen size={14} strokeWidth={2} />
                {showTutorial[activeTab] ? 'Masquer l\'aide' : 'Guide'}
              </button>
            </div>

            {/* Tutorial */}
            {showTutorial[activeTab] && currentTuto && (
              <div className={styles.tutorial}>
                <div className={styles.tutorialHeader}>
                  <HelpCircle size={15} strokeWidth={2} />
                  <strong>{currentTuto.title}</strong>
                </div>
                <ol className={styles.tutorialList}>
                  {currentTuto.steps.map((step, i) =>
                    typeof step === 'string' ? (
                      <li key={i}>{step}</li>
                    ) : (
                      <li key={i}>
                        <strong>{step.strong}</strong> {step.text}
                      </li>
                    )
                  )}
                </ol>
              </div>
            )}

            {/* Champs */}
            <div className={styles.fieldsGrid}>
              {currentTab?.fields.map((field) => (
                <FieldRow
                  key={field}
                  field={field}
                  value={config[field]}
                  meta={FIELD_META[field]}
                  show={!!showPasswords[field]}
                  disabled={isSaving}
                  onChange={handleChange}
                  onToggle={() => togglePassword(field)}
                />
              ))}
            </div>

            {/* Actions */}
            <div className={styles.formActions}>
              <button
                type="submit"
                className={styles.saveBtn}
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <Loader2 size={16} className={styles.savingSpinner} />
                    Sauvegarde…
                  </>
                ) : (
                  <>
                    <Save size={16} strokeWidth={2} />
                    Sauvegarder
                  </>
                )}
              </button>
            </div>
          </form>
        </main>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// SOUS-COMPOSANT : CHAMP
// ─────────────────────────────────────────────────────────────
const FieldRow = ({ field, value, meta, show, disabled, onChange, onToggle }) => {
  const isFilled = value?.trim().length > 0;
  return (
    <div className={styles.formGroup}>
      <label htmlFor={field} className={styles.label}>
        {meta.label}
        <span className={styles.required} aria-hidden="true">*</span>
        {isFilled && <CheckCircle2 size={12} strokeWidth={2.5} className={styles.fieldOk} />}
      </label>
      <div className={`${styles.inputWrap} ${isFilled ? styles.inputWrapFilled : ''}`}>
        <input
          id={field}
          name={field}
          type={show ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          disabled={disabled}
          placeholder={meta.placeholder}
          autoComplete="new-password"
          className={styles.input}
        />
        <button
          type="button"
          className={styles.eyeBtn}
          onClick={onToggle}
          disabled={disabled}
          aria-label={show ? 'Masquer' : 'Afficher'}
          tabIndex={-1}
        >
          {show
            ? <EyeOff size={16} strokeWidth={2} />
            : <Eye    size={16} strokeWidth={2} />}
        </button>
      </div>
    </div>
  );
};

export default ConfigPage;